import type { ParsedHierarchy } from "./hierarchy";

export interface ExistingCategoryState {
  groups: { label: string; isActive: boolean }[];
  categories: { label: string; groupLabel: string; isActive: boolean }[];
}

export interface CategoryDiff {
  addedGroups: string[];
  addedCategories: { label: string; group: string }[];
  /** Active in the app but no longer in Excel → would be deactivated (never deleted). */
  removedGroups: string[];
  removedCategories: { label: string; group: string }[];
  /** Present in both, but under a different group in Excel. */
  movedCategories: { label: string; from: string; to: string }[];
  /** Inactive in the app and present again in Excel. */
  reactivated: string[];
}

export function isEmptyDiff(diff: CategoryDiff): boolean {
  return Object.values(diff).every((list) => list.length === 0);
}

/** Changes that alter how existing data is grouped or offered; they need explicit confirmation. */
export function hasDestructiveChanges(diff: CategoryDiff): boolean {
  return diff.removedGroups.length + diff.removedCategories.length + diff.movedCategories.length > 0;
}

export function computeCategoryDiff(existing: ExistingCategoryState, parsed: ParsedHierarchy): CategoryDiff {
  const incomingGroups = new Set(parsed.groups.map((g) => g.label));
  const incomingCategories = new Map<string, string>();
  for (const g of parsed.groups) for (const l of g.leaves) incomingCategories.set(l.label, g.label);

  const existingGroups = new Map(existing.groups.map((g) => [g.label, g]));
  const existingCategories = new Map(existing.categories.map((c) => [c.label, c]));

  const diff: CategoryDiff = {
    addedGroups: [],
    addedCategories: [],
    removedGroups: [],
    removedCategories: [],
    movedCategories: [],
    reactivated: [],
  };

  for (const label of incomingGroups) {
    const current = existingGroups.get(label);
    if (!current) diff.addedGroups.push(label);
    else if (!current.isActive) diff.reactivated.push(label);
  }
  for (const [label, group] of existingGroups) {
    if (group.isActive && !incomingGroups.has(label)) diff.removedGroups.push(label);
  }
  for (const [label, group] of incomingCategories) {
    const current = existingCategories.get(label);
    if (!current) diff.addedCategories.push({ label, group });
    else {
      if (!current.isActive) diff.reactivated.push(label);
      if (current.groupLabel !== group) diff.movedCategories.push({ label, from: current.groupLabel, to: group });
    }
  }
  for (const [label, category] of existingCategories) {
    if (category.isActive && !incomingCategories.has(label)) {
      diff.removedCategories.push({ label, group: category.groupLabel });
    }
  }
  return diff;
}
